<template>
  <div class="h-full bg-gray-50 flex flex-col">
    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-primary-600 text-xl" />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex-1 flex items-center justify-center p-6">
      <div class="text-center">
        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="text-red-400 text-2xl mb-2" />
        <p class="text-sm text-gray-600">{{ error }}</p>
        <button @click="fetchTask" class="mt-3 text-sm text-primary-600 hover:text-primary-700">Retry</button>
      </div>
    </div>

    <!-- Task Loaded -->
    <template v-else-if="task">
      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <!-- Snooze Alert -->
        <div v-if="task.status === TaskStatus.SNOOZED" class="bg-yellow-50 border border-amber-200 rounded-lg p-3">
          <div class="flex justify-between items-center">
            <div class="flex items-center gap-2">
              <FontAwesomeIcon :icon="['fas', 'clock']" class="text-amber-600 text-sm" />
              <span class="text-sm font-medium text-amber-900">Task is Snoozed</span>
            </div>
            <button
              @click="triggerUnsnooze"
              :disabled="unsnoozing"
              class="px-3 py-1 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400
                     text-white rounded text-xs font-medium"
            >
              {{ unsnoozing ? 'Unsnoozing...' : 'Unsnooze' }}
            </button>
          </div>
        </div>

        <!-- Task Review Widget -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-primary-50 px-4 py-2.5 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'circle-info']" class="mr-2 text-purple-600" />
              {{ task.task_type_name || 'Review' }}
            </h3>
          </div>
          <div class="p-4">
            <Task_Widget_Parent
              :task="task"
              @can_submit="set_can_submit"
            />
          </div>
        </div>

        <!-- Work Activities Section -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'clipboard-check']" class="mr-2 text-gray-600" />
              Work Activities
            </h3>
          </div>
          <div class="p-4 space-y-4">
            <div class="border-b border-gray-100 pb-4">
              <WorkLogger
                :opportunities="task.opportunities"
                :task-id="task.id"
                :task-type-id="task.task_type"
                :task-metadata="task.metadata"
                @work-submitted="$refs.workTimeline?.fetchWorkItems()"
              />
            </div>
            <div>
              <h4 class="text-xs font-medium text-gray-500 mb-2">Activity History</h4>
              <Work_Timeline
                ref="workTimeline"
                :task-id="task.id"
                :task-type-id="task.task_type"
                :task-type-name="task.task_type_name"
                :task-metadata="task.metadata || {}"
                :opportunities="task.opportunities"
              />
            </div>
          </div>
        </div>

        <!-- Action Selection -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <label class="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'clipboard-list']" class="mr-2 text-primary-600" />
            Select Action
          </label>
          <select
            v-model="selectedAction"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          >
            <option disabled value="">Please select an action</option>
            <option v-for="action in activeActions" :key="action.id" :value="action">
              {{ action.name }}
            </option>
          </select>

          <div v-if="selectedAction && selectedAction.task_instruction_type_id === InstructionType.SNOOZE"
               class="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <label class="block text-xs font-medium text-gray-700 mb-1">Snooze Until</label>
            <input
              v-model="snoozeUntil"
              type="datetime-local"
              class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              :min="minSnoozeDate"
            />
          </div>
        </div>

        <!-- Additional Notes -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <label class="block text-sm font-semibold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'message']" class="mr-2 text-green-600" />
            Notes
          </label>

          <div v-if="task.metadata?.previous_task_completed_notes?.length > 0" class="space-y-2 mb-3">
            <div v-for="(note, index) in task.metadata.previous_task_completed_notes" :key="`note-${index}`">
              <div v-if="note.data" class="bg-primary-50 rounded p-3 border border-blue-100">
                <p class="text-xs text-gray-700">{{ note.data }}</p>
                <div class="mt-1 flex items-center text-[10px] text-gray-500">
                  <span class="font-medium text-gray-700">{{ note.appuser }}</span>
                  <span class="mx-1">&middot;</span>
                  <span>{{ note.created_at }}</span>
                </div>
              </div>
            </div>
          </div>

          <textarea
            v-model="additionalData"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
                   focus:ring-2 focus:ring-blue-dark focus:border-transparent resize-none"
            placeholder="Additional notes..."
            rows="3"
          ></textarea>
        </div>

        <Field_Errors :received_errors="field_errors" />

        <!-- Action History — opportunity-scoped: prior coders' decisions
             across all review levels (1LR→2LR→3LR→Confirm), not just this
             task. Mirrors how Work Activity History spans tiers. -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <div class="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon :icon="['fas', 'clock-rotate-left']" class="mr-2 text-gray-600" />
              Action History
            </h3>
          </div>
          <div class="p-4">
            <Coding_Action_History :task-id="task.id" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="bg-white border-t border-gray-200 px-4 py-3 shrink-0">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <button
              @click="submit"
              :disabled="!canSubmit || submitting"
              class="px-5 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium
                     hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed
                     transition-colors flex items-center"
            >
              <FontAwesomeIcon v-if="submitting" :icon="['fas', 'spinner']" class="animate-spin mr-2" />
              <FontAwesomeIcon v-else :icon="['fas', 'check']" class="mr-2" />
              Submit
            </button>
            <slot name="extra-footer-buttons" />
          </div>
          <div v-if="!can_submit && selectedAction" class="text-xs text-amber-600 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'circle-exclamation']" class="mr-1" />
            {{ can_submit_message }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Task_Widget_Parent from '@/components/Portal/Task_Tables/Task_Widget_Parent.vue';
import WorkLogger from '@/components/Portal/Task_Tables/Work_Widgets/WorkLogger.vue';
import Work_Timeline from '@/components/Portal/Task_Tables/Work_Widgets/Work_Timeline.vue';
import Coding_Action_History from './Coding_Action_History.vue';
import Field_Errors from '@/components/widgetcomponents/Field_Errors.vue';
import { submitAction } from '@/services/api/tasks';
import { InstructionType, TaskStatus } from '@/constants/instructionTypes';

export default {
  name: 'Coding_Task_Workspace',
  components: {
    FontAwesomeIcon,
    Task_Widget_Parent,
    WorkLogger,
    Work_Timeline,
    Coding_Action_History,
    Field_Errors,
  },
  props: {
    taskId: { type: Number, required: true },
    actingAsMemberEntityId: { type: Number, default: null },
  },
  emits: ['task-completed', 'task-loaded'],
  data() {
    return {
      InstructionType,
      TaskStatus,
      task: null,
      loading: true,
      error: null,
      selectedAction: '',
      additionalData: '',
      can_submit: false,
      can_submit_message: 'Must complete task action before submitting',
      field_errors: [],
      snoozeUntil: '',
      unsnoozing: false,
      submitting: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    activeActions() {
      // Gates mirror the Portal's Task_Side_Panel.vue: an action only shows
      // when it's valid for the task's current state. Keep in sync with that
      // component (shared business rule; not yet centralized).
      if (!this.task?.available_action_types) return [];
      return this.task.available_action_types.filter(action => {
        if (action.active === false) return false;
        // Unsnooze (7): only when snoozed (status 4). Snooze (6): only when not.
        if (action.task_instruction_type_id === InstructionType.UNSNOOZE && this.task.status !== TaskStatus.SNOOZED) return false;
        if (action.task_instruction_type_id === InstructionType.SNOOZE && this.task.status === TaskStatus.SNOOZED) return false;
        if (action.task_instruction_type_id === InstructionType.HOLD && this.task.status !== TaskStatus.ACTIVE) return false;
        if (action.task_instruction_type_id === InstructionType.RELEASE_HOLD && this.task.status !== TaskStatus.ON_HOLD) return false;
        if (action.task_instruction_type_id === InstructionType.CODER_RESPONSE &&
            this.task?.metadata?.reassignment_type !== 'qa_returned') return false;
        return true;
      });
    },
    minSnoozeDate() {
      const date = new Date();
      date.setHours(date.getHours() + 1);
      return date.toISOString().slice(0, 16);
    },
    canSubmit() {
      if (!this.selectedAction) return false;
      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE) return !!this.snoozeUntil;
      return this.can_submit;
    },
  },
  watch: {
    taskId: {
      immediate: true,
      handler() { this.fetchTask(); },
    },
  },
  methods: {
    async fetchTask() {
      this.loading = true;
      this.error = null;
      this.task = null;
      this.selectedAction = '';
      this.additionalData = '';
      this.field_errors = [];
      try {
        const response = await axios.get(`/api/tasks/${this.taskId}/`);
        this.task = response.data;
        this.$emit('task-loaded', this.task);
      } catch (err) {
        this.error = 'Failed to load task details';
        console.error('Failed to fetch task:', err);
      } finally {
        this.loading = false;
      }
    },
    set_can_submit(value, message) {
      this.can_submit = value;
      this.can_submit_message = message;
    },
    triggerUnsnooze() {
      const unsnoozeAction = this.activeActions.find(a => a.task_instruction_type_id === InstructionType.UNSNOOZE);
      if (unsnoozeAction) {
        this.selectedAction = unsnoozeAction;
        this.submit();
      }
    },
    async submit() {
      this.field_errors = [];
      if (!this.selectedAction) {
        this.field_errors = ['Please select an action'];
        return;
      }

      const metadata = { additionalData: this.additionalData };

      if (this.selectedAction.task_instruction_type_id === InstructionType.SNOOZE) {
        if (!this.snoozeUntil) {
          this.field_errors = ['Please select a date to snooze until'];
          return;
        }
        metadata.snooze_until = new Date(this.snoozeUntil).toISOString();
      }

      this.submitting = true;
      if (this.selectedAction.task_instruction_type_id === InstructionType.UNSNOOZE) {
        this.unsnoozing = true;
      }

      try {
        const result = await submitAction({
          task_id: this.task.id,
          task_action_type_id: this.selectedAction.id,
          acting_as_member_entity_id: this.actingAsMemberEntityId || undefined,
          metadata,
        });
        if (result.already_completed) {
          this.field_errors = ['This task was already completed'];
        }
        this.$emit('task-completed', { taskId: this.task.id, response: result });
      } catch (err) {
        console.error('Submit failed:', err);
        this.field_errors = ['Failed to submit action'];
      } finally {
        this.submitting = false;
        this.unsnoozing = false;
      }
    },
  },
};
</script>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 #F3F4F6;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #F3F4F6;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 3px;
}
</style>
